import {Injectable} from '@angular/core';
import * as AWS from 'aws-sdk';
import {CredentialsOptions} from 'aws-sdk/lib/credentials';
import {Observable} from 'rxjs';
import {Object} from 'aws-sdk/clients/s3';
import {BaseService} from "./BaseService";

/**
 * AWS S3 Connection Service Class.
 * This service manages the connection to an AWS S3 bucket
 */
@Injectable({
  providedIn: 'root'
})
export class S3ManagerService extends BaseService {

  // ****************************
  //      PRIVATE PARAMETERS
  public protocol!: 'http' | 'https';
  // ****************************
  /**
   * The AWS bucket containing the installer
   */
  readonly AWS_BUCKET = 'resources.inntea.it';
  /**
   * The AWS region of the bucket containing the installer
   */
  readonly AWS_REGION = 's3.eu-west-1';
  /**
   * Protocol used to connect to the AWS S3 bucket
   */
  readonly AWS_PROTOCOL = 'https';
  // ****************************
  private bucket!: string;
  private region!: string;
  private awsCredentials!: CredentialsOptions;

  // ****************************
  //   AWS S3 connection fields
  private connectionOptions!: AWS.S3.Types.ClientConfiguration;
  private s3?: AWS.S3;
  private initialized = false;

  cleanupServiceFields(): void {
    this.initialized = false;
    delete this.s3;
  }

  /**
   * Service initialization.
   * This operation is required before listing or downloading bucket elements.
   * @param bucket s3 bucket
   * @param region s3 region
   * @param accessKeyId access key id
   * @param secretAccessKey secret access key
   * @param ssl true if connection has to be encrypted, false otherwise
   */
  public setConnectionParameters(bucket: string, region: string, accessKeyId: string, secretAccessKey: string, ssl: boolean) {
    this.bucket = bucket;
    this.region = region;
    this.protocol = (ssl) ? 'https' : 'http';

    this.awsCredentials = {
      accessKeyId: accessKeyId,
      secretAccessKey: secretAccessKey
    };

    this.connectionOptions = {
      apiVersion: '2006-03-01',
      credentials: this.awsCredentials,
      endpoint: `${this.protocol}://${this.region}.amazonaws.com`,
      sslEnabled: ssl
    };

    this.s3 = new AWS.S3(this.connectionOptions);

    // The initialization is now completed
    this.initialized = true;
  }

  /**
   * The method returns an observable that can provide the list of the objects in the bucket,
   * under the passed prefix
   * @param prefix the elements location in the bucket
   */
  public listObjects(prefix: string): Observable<any> {

    const connectionParams = {
      Bucket: this.bucket,
      Prefix: prefix,
      Delimiter: '/',
      StartAfter: prefix
    };

    return new Observable<any>((observer) => {
      if (this.initialized && !!this.s3) {

        // Listing elements
        this.s3.listObjectsV2(connectionParams, (err, data) => {
          if (err) {
            // something wrong, rejecting promise
            observer.error(err);
          } else {
            // ok, resolving promise and passing objects list
            observer.next(data.Contents);
          }
        });
      } else {
        observer.error('AWS S3 Service not initialized.');
      }

      return {
        unsubscribe(): void {
        }
      };
    });
  }

  /**
   * The method returns the url for a selected object. The object is passed as a param
   * @param element the object to download
   */
  public downloadElement(element: Object): string | null {
    if (this.initialized && !!this.s3) {
      return this.s3.getSignedUrl('getObject', {
        Bucket: this.bucket,
        Key: element.Key,
        Expires: 3600
      });
    } else {
      return null;
    }

  }

  public getSignedUrlByKey(key: string): string | null {
    if (this.initialized && !!this.s3) {
      return this.s3.getSignedUrl('getObject', {
        Bucket: this.bucket,
        Key: key,
        Expires: 3600
      });
    } else {
      return null;
    }

  }

}
